import { Component, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  Feature,
  MultiPolygon,
  Polygon,
  area,
  bbox,
  bboxPolygon,
  featureCollection,
} from '@turf/turf';
import { AuthStateService } from 'app/core/services/auth-state.service';
import { TimeoutEnum } from 'app/core/enum/timeout.enum';
import { AnalyticsService } from 'app/core/services/analytics.service';
import { ConfirmacaoModalService } from 'app/core/services/confirmacaoModal.service';
import { LoadingSpinnerService } from 'app/core/services/loading-spinner.service';
import { UserTalhaoListService } from 'app/core/services/user-talhao-list.service';
import {
  CREATE_FIELD_ROUTE,
  CreateFieldNavigationState,
} from 'app/shared/models/create-field-navigation-state';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';

interface ReferenceCoordinate {
  lat: number;
  lng: number;
}
@Component({
  standalone: false,
  selector: 'app-lista-talhoes',
  templateUrl: './lista-talhoes.component.html',
  styleUrls: ['./lista-talhoes.component.scss'],
})
export class ListaTalhoesComponent implements OnInit {
  mostrarModalConfirmacao = false;
  mensagemModalConfirmacao = '';
  tipoModalConfirmacao = '';
  tituloModalConfirmacao = '';
  modalTranslateButtonConfirmText = '';
  modalTranslateButtonCancelText = '';
  idArquivoSelecionadoExcluir: any;
  STATUS_CONFIRMED_DELETE = 'confirmado';
  fetchFieldData: Subject<boolean> = new Subject();
  updateFieldIdToBeDeletedSubject: Subject<string> = new Subject();

  valueInputSearch: string;
  hideAll = true;
  totalFields = 0;
  valueOpenTalhaoById = false;

  fieldId: string | null;

  private readonly authState = inject(AuthStateService);

  statusTalhao: 'create' | 'view' | 'edit' | 'delete';
  showRecommendationMapUpload = false;
  showModalSubject: Subject<boolean> = new Subject();
  referenceCoordinateSubject: Subject<ReferenceCoordinate> = new Subject();
  userId: string;
  constructor(
    public translate: TranslateService,
    public router: Router,
    public translateService: TranslateService,
    public confirmacaoModalService: ConfirmacaoModalService,
    public talhaoService: UserTalhaoListService,
    private readonly toastr: ToastrService,
    public loadingSpinner: LoadingSpinnerService,
    public readonly analyticsService: AnalyticsService,
  ) {
    const usuarioLogado = this.authState.getUser();
    this.userId = usuarioLogado?.id;
    this.analyticsService.sendPageView('/my-fields/');
    this.analyticsService.trackEvent(
      'Open list my field page',
      'List My Field',
    );
  }

  ngOnInit(): void {
    this.confirmacaoModalService.changeData(null);
    this.confirmacaoModalService.currentData.subscribe((data) => {
      if (data != null) {
        if (this.statusTalhao === 'delete' && this.fieldId) {
          if (data.message === this.STATUS_CONFIRMED_DELETE) {
            this.updateFieldIdToBeDeletedSubject.next(this.fieldId);
            this.deleteField(this.fieldId);
          }
          this.statusTalhao = undefined;
          this.fieldId = null;
        }
      }
    });
    this.analyticsService.trackEvent('accessed_the_field_page', 'List Field');
  }

  importSHP(): void {
    this.showModalSubject.next(true);
    this.analyticsService.trackEvent(
      'Clicked on import SHP button',
      'List My Field',
    );
  }

  recebeOEventEmiterParaAbrirTalhoesPorId() {
    this.valueOpenTalhaoById = true;

    return (talhaoId: string | null, talhaoIdToBeDeleted: string | null) => {
      if (talhaoId != talhaoIdToBeDeleted) {
        this.fieldId = talhaoId;
        this.statusTalhao = 'view';
        this.analyticsService.trackEvent('Opened field by id', 'List My Field');
      }
    };
  }

  recebeOEventEmiterParaEditarTalhoesPorId() {
    this.valueOpenTalhaoById = true;

    return (talhaoId: string | null, talhaoIdToBeDeleted: string | null) => {
      if (talhaoId != talhaoIdToBeDeleted) {
        this.fieldId = talhaoId;
        this.statusTalhao = 'edit';
      }
    };
  }

  changeTalhaoIdOnDelete() {
    return (talhaoId: string | null, talhaoIdToBeDeleted: string | null) => {
      if (talhaoId != talhaoIdToBeDeleted) {
        this.fieldId = talhaoId;
        this.statusTalhao = 'delete';
        this.translateConfirmationMessage();
        this.analyticsService.trackEvent(
          'Clicked on delete field',
          'List My Field',
        );
      }
    };
  }

  onEmitVoltarParaListaDeTalhoes() {
    this.valueOpenTalhaoById = false;
    return () => {
      this.fieldId = null;
    };
  }

  atualizarBusca(event): void {
    this.valueInputSearch = event.target.value;
    this.analyticsService.trackEvent(
      'Clicked on search field',
      'List My Field',
    );
  }

  createTalhao(feature?: Feature<Polygon | MultiPolygon>): void {
    const state: CreateFieldNavigationState = { feature };
    this.router.navigate([CREATE_FIELD_ROUTE], { state });
    this.analyticsService.trackEvent(
      'Clicked on create field',
      'List My Field',
    );
  }

  translateConfirmationMessage() {
    this.translateService
      .get('global.alert.delete-title')
      .subscribe((res: string) => {
        this.mostrarModalConfirmacao = true;
        this.mensagemModalConfirmacao = '';
        this.tituloModalConfirmacao = res;
        this.tipoModalConfirmacao = 'warning';
        this.modalTranslateButtonConfirmText = 'global.delete';
        this.modalTranslateButtonCancelText = 'global.cancel';
      });
  }

  dashboard() {
    this.router.navigate(['/']);
  }

  deleteField(fieldId) {
    this.talhaoService.deleteTalhao(fieldId).subscribe(
      (result) => {
        this.translateService
          .get('global.alert.success-delete')
          .subscribe((res: string) => {
            this.toastr.success(res, '', {
              positionClass: 'toast-top-full-width',
              disableTimeOut: false,
            });
          });
        this.totalFields--;
        if (this.totalFields === 0) {
          this.showAlertMessage();
        } else {
          this.fetchFieldData.next(true);
        }
        this.analyticsService.trackEvent('Deleted a field', 'List My Field');
      },
      (error) => {
        this.analyticsService.trackEvent(
          'Error when deleting field',
          'List My Field',
        );
        this.updateFieldIdToBeDeletedSubject.next('');
        let errorMessage = '';
        switch (error.status) {
          case 409:
            errorMessage = 'fields.fields-not-found';
            break;

          default:
            errorMessage = 'global.validate.message-error-delete';
        }
        if (error.error !== undefined) {
          this.translateService
            .get(errorMessage)
            .subscribe((msgErro: string) => {
              this.toastr.error(msgErro, '', {
                positionClass: 'toast-top-full-width',
                disableTimeOut: false,
              });
            });
        } else {
          this.translateService
            .get('global.validate.message-error-delete')
            .subscribe((msgErro: string) => {
              this.toastr.error(msgErro, '', {
                positionClass: 'toast-top-full-width',
                disableTimeOut: false,
              });
            });
        }
      },
    );
  }

  actionHome() {
    this.router.navigate(['/']);
  }

  showAlertMessage() {
    this.translateService
      .get('global.validate.all-deleted-fields')
      .subscribe((res: string) => {
        this.toastr.warning(res, '', {
          positionClass: 'toast-top-full-width',
          disableTimeOut: false,
        });
      });
  }
  onReceiveRecommendationMap(shapefile: {
    propertiesToUse?: string;
    shapefileArray: Feature<Polygon | MultiPolygon>[];
  }) {
    let features = shapefile.shapefileArray;
    this.createTalhao(features[0]);
    this.analyticsService.trackEvent(
      'uploaded_shapefile_to_create_field',
      'Import ShapeFile',
    );
  }
}
